import {
  Button,
  IconButton,
  DropdownMenu,
  DropdownItem,
  Tooltip,
} from '@dev-spendesk/grapes';
import classNames from 'classnames';
import { useEffect, useState } from 'react';

import { useTranslation } from 'common/hooks/useTranslation';
import { useCompanyId } from 'modules/app/hooks/useCompanyId';
import { ExpenseAccountAdvice } from 'modules/bookkeep/components/AccountAdvice';
import { type IntegrationStatusWithIntegration } from 'modules/bookkeep/integration/status';
import {
  type MutationQueryState,
  type QueryState,
} from 'src/core/api/queryState';
import { CreateExpenseAccountModal } from 'src/core/modules/bookkeep/components/CreateExpenseAccountModal';
import { DeleteExpenseAccountModal } from 'src/core/modules/bookkeep/components/DeleteExpenseAccountModal';
import { UpdateExpenseAccountModal } from 'src/core/modules/bookkeep/components/UpdateExpenseAccountModal';
import { apiUrl } from 'src/core/utils/api';

import { BulkImportExpenseAccountModal } from './BulkImportExpenseAccountModal';
import { DefaultExpenseAccountForm } from './DefaultExpenseAccountForm';
import { ExpenseAccountEmptyState } from './ExpenseAccountEmptyState';
import { ExpenseAccountErrorModal } from './ExpenseAccountErrorModal';
import styles from './ExpenseAccountLocalOnlySection.module.css';
import { ExpenseAccountTable } from './ExpenseAccountTable';
import {
  getExpenseAccountsDescriptionI18nKey,
  getExpenseAccountsHelpCenterLink,
} from './translations';
import {
  reshapeToFormData,
  useExpenseAccountsMutation,
} from './useImportExpenseAccountsMutation';
import {
  type ExpenseAccount,
  type ExpenseAccountUpdate,
} from '../../../../../accounting';
import { type Result as GetDefaultExpenseAccountQueryResult } from '../../../../hooks/useGetDefaultExpenseAccountQuery';
import { type Result as SetDefaultExpenseAccountMutationResult } from '../../../../hooks/useSetDefaultExpenseAccountMutation';

type Props = {
  expenseAccounts: ExpenseAccount[];
  hasNextPage: boolean;
  fetchNextPage: () => Promise<void>;
  isFetchingMore: boolean;
  clearErrors: () => void;
  integrationStatus: IntegrationStatusWithIntegration;
  isLoading: boolean;
};

type PropsWithDefaultAccounts = Props & {
  setDefaultExpenseAccountQueryState: MutationQueryState<SetDefaultExpenseAccountMutationResult>;
  getDefaultExpenseAccountQueryState: QueryState<GetDefaultExpenseAccountQueryResult>;
  onDefaultChange: (expenseAccount: ExpenseAccountUpdate) => Promise<void>;
};

export const ExpenseAccountLocalOnlyWithDefaultAccountsSection = (
  props: PropsWithDefaultAccounts,
) => {
  return <ExpenseAccountSection {...props} withDefaultAccount />;
};

export const ExpenseAccountLocalOnlySection = (props: Props) => {
  return (
    <ExpenseAccountSection
      {...props}
      withDefaultAccount={false}
      onDefaultChange={async () => {}}
      setDefaultExpenseAccountQueryState={{ status: 'idle' }}
      getDefaultExpenseAccountQueryState={{ status: 'loading' }}
    />
  );
};

enum MODAL_KIND {
  IMPORT = 'import',
  ADD = 'add',
  CONFIRM_DELETE = 'confirmDelete',
  UPDATE = 'update',
  CLOSED = 'closed',
  ERROR = 'error',
}

type ModalState =
  | { kind: MODAL_KIND.CLOSED }
  | { kind: MODAL_KIND.ADD }
  | {
      kind: MODAL_KIND.UPDATE;
      account: ExpenseAccount;
    }
  | {
      kind: MODAL_KIND.CONFIRM_DELETE;
      account: ExpenseAccount;
    }
  | { kind: MODAL_KIND.IMPORT }
  | { kind: MODAL_KIND.ERROR };

const ExpenseAccountSection = ({
  withDefaultAccount,
  setDefaultExpenseAccountQueryState,
  getDefaultExpenseAccountQueryState,
  onDefaultChange,
  expenseAccounts,
  hasNextPage,
  fetchNextPage,
  isFetchingMore,
  clearErrors,
  integrationStatus,
  isLoading,
}: PropsWithDefaultAccounts & { withDefaultAccount: boolean }) => {
  const openImportModal = () => setModalStateFromKind(MODAL_KIND.IMPORT);
  const openManualModal = () => setModalStateFromKind(MODAL_KIND.ADD);
  const closeModals = () => setModalStateFromKind(MODAL_KIND.CLOSED);
  const openErrorModal = () => setModalStateFromKind(MODAL_KIND.ERROR);
  const { t } = useTranslation('global');
  const companyId = useCompanyId();
  const [modalState, setModalState] = useState<ModalState>({
    kind: MODAL_KIND.CLOSED,
  });
  const [importExpenseAccounts, expenseAccountMutationState] =
    useExpenseAccountsMutation();

  useEffect(() => {
    if (expenseAccountMutationState.status === 'error') {
      openErrorModal();
    }
  }, [expenseAccountMutationState.status]);

  const expenseAccountsErrorsList =
    integrationStatus.settingsValidation.expenseAccounts.flatMap(
      ({ id }) => id,
    );

  const defaultExpenseAccount =
    getDefaultExpenseAccountQueryState.status === 'success'
      ? getDefaultExpenseAccountQueryState.data
      : undefined;

  const isDefaultExpenseAccountToggleChecked = Boolean(
    defaultExpenseAccount?.id && !defaultExpenseAccount.isArchived,
  );

  async function handleUpload(file: File, prefixWithCode: boolean) {
    const payload = reshapeToFormData(file, prefixWithCode);
    try {
      await importExpenseAccounts(payload);
      closeModals();
    } catch {
      // caught below as mutationState.error
    }
  }

  function setModalStateFromKind(kind: MODAL_KIND, account?: ExpenseAccount) {
    switch (kind) {
      case MODAL_KIND.IMPORT:
      case MODAL_KIND.ERROR:
      case MODAL_KIND.CLOSED:
      case MODAL_KIND.ADD:
        setModalState({
          kind,
        });
        break;

      case MODAL_KIND.UPDATE:
      case MODAL_KIND.CONFIRM_DELETE:
        if (account) {
          setModalState({ kind, account });
        }

        break;

      default:
        break;
    }
  }

  return (
    <div id="expense-accounts" className="w-full max-w-lg self-center">
      {(() => {
        if (isLoading) {
          return null;
        }

        if (!expenseAccounts.length) {
          return (
            <ExpenseAccountEmptyState
              importAction={openImportModal}
              manualAction={openManualModal}
            />
          );
        }

        return (
          <>
            <div className={styles.header}>
              <div className={styles.headerText}>
                <h3 className="IntegrationAccountingPage__section-title heading-l">
                  {t('bookkeep.integrations.settings.sectionExpenseAccounts')}
                </h3>
                <p
                  className={classNames(styles.headerTextDescription, 'body-m')}
                >
                  {t(
                    getExpenseAccountsDescriptionI18nKey(
                      integrationStatus.integration,
                    ),
                    { integration: integrationStatus.integration },
                  )}
                  &nbsp;|&nbsp;
                  <a
                    href={getExpenseAccountsHelpCenterLink(
                      integrationStatus.integration,
                    )}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t('misc.helpCenterArticle')}
                  </a>
                </p>
              </div>
              <div>
                <DropdownMenu
                  placement="bottom-end"
                  className={styles.headerBtn}
                  options={[
                    {
                      key: MODAL_KIND.ADD,
                      label: t(
                        'bookkeep.integrations.settings.expenseAccountsTable.create',
                      ),
                    },
                    {
                      key: MODAL_KIND.IMPORT,
                      label: t(
                        'bookkeep.integrations.settings.expenseAccountsTable.bulkImport',
                      ),
                    },
                  ]}
                  renderButton={(getToggleButtonProps) => (
                    <Button
                      {...getToggleButtonProps()}
                      text={t(
                        'bookkeep.integrations.settings.expenseAccountsTable.addExpenseAccount',
                      )}
                      variant="primaryBrand"
                    />
                  )}
                  renderOption={(option) => (
                    <DropdownItem label={option.label} />
                  )}
                  onSelect={(option) => setModalStateFromKind(option.key)}
                />
              </div>

              <a
                rel="noopener noreferrer"
                target="_blank"
                href={apiUrl(
                  `/accounting-integration/chart-of-accounts/expense-accounts/export`,
                  companyId,
                )}
                aria-label={t(
                  'bookkeep.integrations.settings.expenseAccountsTable.downloadList',
                )}
              >
                <Tooltip
                  content={t(
                    'bookkeep.integrations.settings.expenseAccountsTable.downloadList',
                  )}
                  triggerAsChild
                >
                  <IconButton
                    iconName="arrow-down-tray"
                    variant="secondaryNeutral"
                    className="ml-8"
                    aria-label=""
                  />
                </Tooltip>
              </a>
            </div>
            {!!expenseAccountsErrorsList.length && (
              <ExpenseAccountAdvice
                showError
                integrationStatus={integrationStatus}
                title={t(
                  'bookkeep.integrations.datev.accountCodeAdvice.expenseAccounts.title',
                  {
                    count: expenseAccountsErrorsList.length,
                  },
                )}
                className={styles.errorCallout}
              />
            )}
            <div className="my-32">
              <ExpenseAccountTable
                expenseAccounts={expenseAccounts}
                expenseAccountsErrorsList={expenseAccountsErrorsList}
                fetchNextPage={fetchNextPage}
                isFetchingMore={isFetchingMore}
                hasFooter={hasNextPage}
                isLoading={isLoading}
                onDelete={(row) =>
                  setModalStateFromKind(MODAL_KIND.CONFIRM_DELETE, row)
                }
                onEdit={(row) => setModalStateFromKind(MODAL_KIND.UPDATE, row)}
              />
            </div>
          </>
        );
      })()}
      {withDefaultAccount ? (
        <DefaultExpenseAccountForm
          integrationStatus={integrationStatus}
          getDefaultExpenseAccountQueryState={
            getDefaultExpenseAccountQueryState
          }
          setDefaultExpenseAccountQueryState={
            setDefaultExpenseAccountQueryState
          }
          onDefaultChange={onDefaultChange}
          defaultExpenseAccount={defaultExpenseAccount}
          isInitialToggleChecked={isDefaultExpenseAccountToggleChecked}
        />
      ) : null}
      <CreateExpenseAccountModal
        integrationStatus={integrationStatus}
        isOpen={modalState.kind === MODAL_KIND.ADD}
        onClose={() => setModalStateFromKind(MODAL_KIND.CLOSED)}
      />
      <UpdateExpenseAccountModal
        integrationStatus={integrationStatus}
        isOpen={modalState.kind === MODAL_KIND.UPDATE}
        onClose={() => setModalStateFromKind(MODAL_KIND.CLOSED)}
        account={
          modalState.kind === MODAL_KIND.UPDATE ? modalState.account : undefined
        }
      />
      <BulkImportExpenseAccountModal
        isOpen={modalState.kind === MODAL_KIND.IMPORT}
        onUpload={handleUpload}
        onCancel={() => {
          setModalStateFromKind(MODAL_KIND.CLOSED);
          clearErrors();
        }}
      />
      {modalState.kind === MODAL_KIND.ERROR &&
      expenseAccountMutationState.status === 'error' &&
      expenseAccountMutationState.error.type === 'RequestError' ? (
        <ExpenseAccountErrorModal
          action={openImportModal}
          close={closeModals}
          errors={expenseAccountMutationState.error.data.errors}
          isOpen={expenseAccountMutationState.error.data.errors.length !== 0}
        />
      ) : null}
      <DeleteExpenseAccountModal
        account={
          modalState.kind === MODAL_KIND.CONFIRM_DELETE
            ? modalState.account
            : undefined
        }
        integrationStatus={integrationStatus}
        isOpen={modalState.kind === MODAL_KIND.CONFIRM_DELETE}
        onClose={() => setModalStateFromKind(MODAL_KIND.CLOSED)}
      />
    </div>
  );
};
